import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Button, Alert, IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Tabs, Tab, Divider,
  FormControlLabel, Radio, RadioGroup, MenuItem
} from '@mui/material';
import { Refresh as RefreshIcon, LocalShipping as OrderIcon, Inventory as InventoryIcon, PlayArrow as ProcessIcon } from '@mui/icons-material';
import equipmentService from '../../services/equipmentService';
import SkeletonBlock from '../ui/SkeletonBlock';
import EmptyState from '../ui/EmptyState';
import { useToast } from '../ToastProvider';
import { format } from 'date-fns';



// Reusable component to display request details in dialogs
const RequestSummary = ({ request }) => {
  if (!request) return null;
  return (
    <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
      <Typography variant="subtitle2" gutterBottom>Request Details</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, fontSize: '0.875rem' }}>
        <Box><strong>Requester:</strong> {request.requesterName}</Box>
        <Box><strong>Department:</strong> {request.departmentName}</Box>
        <Box><strong>Category:</strong> {request.categoryName}</Box>
        <Box><strong>Item:</strong> {request.subcategoryName}</Box>
        <Box><strong>Quantity:</strong> {request.quantity}</Box>
        <Box><strong>Est. Price:</strong> {request.estimatedPrice ? `${request.estimatedPrice} EUR` : '-'}</Box>
        {request.preferredBrand && <Box><strong>Preferred Brand:</strong> {request.preferredBrand}</Box>}
        {request.preferredModel && <Box><strong>Preferred Model:</strong> {request.preferredModel}</Box>}
        {request.justification && <Box sx={{ gridColumn: '1 / -1' }}><strong>Justification:</strong> {request.justification}</Box>}
      </Box>
    </Box>
  );
};

export default function EquipmentOrdersPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusTab, setStatusTab] = useState('Approved');
  const toast = useToast();

  // Unified Process Request dialog (only 2 modes: inventory or order)
  const [processDlg, setProcessDlg] = useState({
    open: false,
    request: null,
    mode: 'inventory', // 'inventory' | 'order'
    // For inventory mode
    existingItemId: '',
    // For order mode
    supplier: '',
    actualPrice: '',
    expectedDeliveryDate: '',
    // Common
    notes: ''
  });
  // Available items for the current request's subcategory
  const [availableItems, setAvailableItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      console.log('Fetching equipment requests with status:', statusTab);
      const data = await equipmentService.getAllRequests(statusTab);
      console.log('API response:', data);
      // api.js response interceptor already extracts response.data
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Equipment orders API error:', e.response?.status, e.response?.data);
      setError(e.response?.data?.message || e.response?.data || `Failed to load requests (${e.response?.status || 'unknown'})`);
    } finally {
      setLoading(false);
    }
  }, [statusTab]);

  useEffect(() => { loadData(); }, [loadData]);

  // Open Process Request dialog (for Approved requests)
  const openProcessDialog = async (request) => {
    // Load available items first
    setLoadingItems(true);
    setAvailableItems([]);

    let items = [];
    try {
      items = await equipmentService.getAvailableItems(request.subcategoryId);
      items = Array.isArray(items) ? items : [];
      setAvailableItems(items);
    } catch (e) {
      console.error('Failed to load available items', e);
    } finally {
      setLoadingItems(false);
    }

    // Set default mode based on inventory availability
    const defaultMode = items.length > 0 ? 'inventory' : 'order';

    setProcessDlg({
      open: true,
      request,
      mode: defaultMode,
      existingItemId: '',
      supplier: '',
      actualPrice: request.estimatedPrice || '',
      expectedDeliveryDate: '',
      notes: ''
    });
  };

  // Handle Process Request action (only 2 modes: inventory or order)
  const handleProcess = async () => {
    const { request, mode } = processDlg;

    try {
      if (mode === 'inventory') {
        // Fulfill directly from inventory
        await equipmentService.fulfillRequest(request.id, {
          existingItemId: parseInt(processDlg.existingItemId),
          notes: processDlg.notes || null
        });
        toast.success('Request fulfilled from inventory');
      } else if (mode === 'order') {
        // Mark as ordered - equipment will be registered in inventory when delivered
        // and then assigned via Equipment Assignments (which will auto-fulfill this request)
        await equipmentService.orderEquipment(request.id, {
          supplier: processDlg.supplier,
          actualPrice: processDlg.actualPrice ? parseFloat(processDlg.actualPrice) : null,
          expectedDeliveryDate: processDlg.expectedDeliveryDate || null
        });
        toast.success('Equipment ordered. When delivered, register it in Equipment Inventory and assign via Equipment Assignments.');
      }

      // Reset dialog
      setProcessDlg({ open: false, request: null, mode: 'inventory', existingItemId: '', supplier: '', actualPrice: '', expectedDeliveryDate: '', notes: '' });
      setAvailableItems([]);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.message || e.response?.data || 'Failed to process request');
    }
  };

  // Check if Process button should be disabled
  const isProcessDisabled = () => {
    const { mode, existingItemId, supplier } = processDlg;
    if (mode === 'inventory') return !existingItemId;
    if (mode === 'order') return !supplier;
    return false;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Equipment Orders</Typography>
        <Tooltip title="Refresh"><IconButton onClick={loadData}><RefreshIcon /></IconButton></Tooltip>
      </Box>

      <Tabs value={statusTab} onChange={(e, v) => setStatusTab(v)} sx={{ mb: 2 }}>
        <Tab value="Approved" label="Approved (Ready for Order)" />
        <Tab value="Ordered" label="Ordered (Pending Delivery)" />
        <Tab value="Fulfilled" label="Fulfilled" />
      </Tabs>

      {loading && <SkeletonBlock lines={5} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!loading && !error && items.length === 0 && (
        <EmptyState icon={<OrderIcon sx={{ fontSize: 48 }} />} title="No requests" description={`No equipment requests with status "${statusTab}".`} />
      )}

      {!loading && items.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Requester</TableCell>
                <TableCell>Item</TableCell>
                {statusTab === 'Approved' && <TableCell>Est. Price</TableCell>}
                {statusTab === 'Approved' && <TableCell>Requested</TableCell>}
                {statusTab === 'Ordered' && <TableCell>Actual Price</TableCell>}
                {statusTab === 'Ordered' && <TableCell>Ordered</TableCell>}
                {statusTab === 'Ordered' && <TableCell>Expected</TableCell>}
                {statusTab === 'Fulfilled' && <TableCell>Actual Price</TableCell>}
                {statusTab === 'Fulfilled' && <TableCell>Delivered</TableCell>}
                {statusTab !== 'Fulfilled' && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map(r => (
                <TableRow key={r.id}>
                  <TableCell>{r.id}</TableCell>
                  <TableCell>{r.requesterName || r.employeeName}</TableCell>
                  <TableCell>{r.subcategoryName} {r.preferredBrand && `(${r.preferredBrand})`}</TableCell>
                  {statusTab === 'Approved' && <TableCell>{r.estimatedPrice ? `${r.estimatedPrice} EUR` : '-'}</TableCell>}
                  {statusTab === 'Approved' && <TableCell>{format(new Date(r.requestDate), 'dd.MM.yyyy')}</TableCell>}
                  {statusTab === 'Ordered' && <TableCell>{r.actualPrice ? `${r.actualPrice} EUR` : (r.estimatedPrice ? `~${r.estimatedPrice} EUR` : '-')}</TableCell>}
                  {statusTab === 'Ordered' && <TableCell>{r.orderedDate ? format(new Date(r.orderedDate), 'dd.MM.yyyy') : '-'}</TableCell>}
                  {statusTab === 'Ordered' && <TableCell>{r.expectedDeliveryDate ? format(new Date(r.expectedDeliveryDate), 'dd.MM.yyyy') : '-'}</TableCell>}
                  {statusTab === 'Fulfilled' && <TableCell>{r.actualPrice ? `${r.actualPrice} EUR` : '-'}</TableCell>}
                  {statusTab === 'Fulfilled' && <TableCell>{r.fulfilledDate ? format(new Date(r.fulfilledDate), 'dd.MM.yyyy') : '-'}</TableCell>}
                  {statusTab !== 'Fulfilled' && (
                    <TableCell align="right">
                      {r.status === 'Approved' && (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<ProcessIcon />}
                          onClick={() => openProcessDialog(r)}
                        >
                          Process Request
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Process Request Dialog (for Approved requests) */}
      <Dialog open={processDlg.open} onClose={() => setProcessDlg({ ...processDlg, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>Process Equipment Request</DialogTitle>
        <DialogContent>
          <RequestSummary request={processDlg.request} />
          <Divider sx={{ my: 2 }} />

          {/* Show available items info */}
          {availableItems.length > 0 && (
            <Alert severity="success" icon={<InventoryIcon />} sx={{ mb: 2 }}>
              {availableItems.length} item(s) available in inventory for this type
            </Alert>
          )}
          {!loadingItems && availableItems.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No items available in inventory. You will need to order new equipment.
            </Alert>
          )}

          {/* Mode selection with radio buttons - only 2 options */}
          <Typography variant="subtitle2" gutterBottom>How do you want to process this request?</Typography>
          <RadioGroup
            value={processDlg.mode}
            onChange={e => setProcessDlg({ ...processDlg, mode: e.target.value })}
          >
            <FormControlLabel
              value="inventory"
              control={<Radio />}
              label="Use item from inventory (fulfill immediately)"
              disabled={availableItems.length === 0 || loadingItems}
            />
            <FormControlLabel
              value="order"
              control={<Radio />}
              label="Order new equipment (register in inventory when delivered, then assign)"
            />
          </RadioGroup>

          <Divider sx={{ my: 2 }} />

          {/* Mode-specific fields */}
          {processDlg.mode === 'inventory' && (
            <TextField
              select
              label="Select Item from Inventory"
              fullWidth
              margin="normal"
              value={processDlg.existingItemId}
              onChange={e => setProcessDlg({ ...processDlg, existingItemId: e.target.value })}
              disabled={loadingItems}
              required
            >
              {loadingItems ? (
                <MenuItem value="">Loading...</MenuItem>
              ) : (
                availableItems.map(item => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.brand || 'Unknown'} {item.model || ''} {item.serialNumber ? `(SN: ${item.serialNumber})` : ''}
                  </MenuItem>
                ))
              )}
            </TextField>
          )}

          {processDlg.mode === 'order' && (
            <>
              <TextField label="Supplier" fullWidth margin="normal" required value={processDlg.supplier} onChange={e => setProcessDlg({ ...processDlg, supplier: e.target.value })} />
              <TextField label="Actual Price (EUR)" type="number" fullWidth margin="normal" value={processDlg.actualPrice} onChange={e => setProcessDlg({ ...processDlg, actualPrice: e.target.value })} />
              <TextField label="Expected Delivery Date" type="date" fullWidth margin="normal" InputLabelProps={{ shrink: true }} value={processDlg.expectedDeliveryDate} onChange={e => setProcessDlg({ ...processDlg, expectedDeliveryDate: e.target.value })} />
            </>
          )}

          <TextField label="Notes" fullWidth margin="normal" multiline rows={2} value={processDlg.notes} onChange={e => setProcessDlg({ ...processDlg, notes: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProcessDlg({ ...processDlg, open: false })}>Cancel</Button>
          <Button
            variant="contained"
            color={processDlg.mode === 'order' ? 'primary' : 'success'}
            onClick={handleProcess}
            disabled={isProcessDisabled()}
          >
            {processDlg.mode === 'order' ? 'Place Order' : 'Fulfill'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

